import {
  EFFECTIVE_MAX_DURATION_SECONDS,
  EFFECTIVE_MIN_DURATION_SECONDS,
  type VoiceReferenceFailureCode,
  type VoiceReferenceQualityAnalysis,
  type VoiceReferenceQualityStatus,
} from './voiceReferenceQualityPolicy'

const MIN_BLOB_BYTES = 256

function computeChannelStats(
  channel: Float32Array,
  sampleRate: number,
): {
  meanRms: number
  peak: number
  silenceRatio: number
  speechSeconds: number
  speechRatio: number
} {
  if (channel.length === 0) {
    return { meanRms: 0, peak: 0, silenceRatio: 1, speechSeconds: 0, speechRatio: 0 }
  }
  let sumSq = 0
  let peak = 0
  let silentFrames = 0
  let speechFrames = 0
  const frameSize = 1024
  for (let i = 0; i < channel.length; i += frameSize) {
    let framePeak = 0
    const end = Math.min(i + frameSize, channel.length)
    for (let j = i; j < end; j += 1) {
      const v = Math.abs(channel[j] ?? 0)
      framePeak = Math.max(framePeak, v)
      sumSq += v * v
    }
    peak = Math.max(peak, framePeak)
    if (framePeak < 0.01) silentFrames += 1
    else speechFrames += 1
  }
  const frameCount = Math.max(1, Math.ceil(channel.length / frameSize))
  const meanRms = Math.sqrt(sumSq / channel.length)
  const speechRatio = speechFrames / frameCount
  const speechSeconds = (speechFrames * frameSize) / Math.max(1, sampleRate)
  return { meanRms, peak, silenceRatio: silentFrames / frameCount, speechSeconds, speechRatio }
}

function resolveFailureCode(blockingReasons: string[], warnings: string[]): VoiceReferenceFailureCode {
  if (blockingReasons.includes('no_audio_data')) return 'NO_AUDIO_DATA'
  if (blockingReasons.includes('decode_failed')) return 'DECODE_FAILED'
  if (blockingReasons.includes('duration_too_short')) return 'TOO_SHORT'
  if (blockingReasons.includes('duration_too_long')) return 'TOO_LONG'
  if (blockingReasons.includes('no_speech')) return 'NO_SPEECH'
  if (blockingReasons.includes('too_much_silence')) return 'TOO_MUCH_SILENCE'
  if (warnings.includes('volume_low')) return 'LOW_VOLUME'
  if (warnings.includes('clipping')) return 'CLIPPING'
  return null
}

export async function analyzeReferenceAudioBlob(blob: Blob): Promise<VoiceReferenceQualityAnalysis> {
  if (blob.size <= 0) {
    return buildQualityAnalysis({
      durationMs: 0,
      meanRms: 0,
      peak: 0,
      silenceRatio: 1,
      captureOk: false,
      decodeOk: false,
    })
  }
  if (blob.size < MIN_BLOB_BYTES) {
    return buildQualityAnalysis({
      durationMs: 0,
      meanRms: 0,
      peak: 0,
      silenceRatio: 1,
      captureOk: false,
      decodeOk: false,
      forceNoAudioData: true,
    })
  }

  const arrayBuffer = await blob.arrayBuffer()
  const audioContext = new AudioContext()
  try {
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer.slice(0))
    const channel = audioBuffer.getChannelData(0)
    const { meanRms, peak, silenceRatio, speechSeconds, speechRatio } = computeChannelStats(
      channel,
      audioBuffer.sampleRate,
    )
    const durationMs = Math.round(audioBuffer.duration * 1000)
    return buildQualityAnalysis({
      durationMs,
      meanRms,
      peak,
      silenceRatio,
      captureOk: true,
      decodeOk: true,
      speechSeconds,
      speechRatio,
      sampleRate: audioBuffer.sampleRate,
      channels: audioBuffer.numberOfChannels,
    })
  } catch {
    return buildQualityAnalysis({
      durationMs: 0,
      meanRms: 0,
      peak: 0,
      silenceRatio: 1,
      captureOk: true,
      decodeOk: false,
    })
  } finally {
    await audioContext.close().catch(() => undefined)
  }
}

export async function analyzeReferenceAudioFile(file: File): Promise<VoiceReferenceQualityAnalysis> {
  return analyzeReferenceAudioBlob(file)
}

export function buildQualityAnalysis(args: {
  durationMs: number
  meanRms: number
  peak: number
  silenceRatio: number
  captureOk: boolean
  decodeOk: boolean
  speechSeconds?: number
  speechRatio?: number
  sampleRate?: number
  channels?: number
  forceNoAudioData?: boolean
}): VoiceReferenceQualityAnalysis {
  const blockingReasons: string[] = []
  const warnings: string[] = []
  const durationSec = args.durationMs / 1000

  if (!args.captureOk || args.forceNoAudioData) {
    blockingReasons.push('no_audio_data')
  } else if (!args.decodeOk) {
    blockingReasons.push('decode_failed')
  } else if (args.durationMs <= 0) {
    blockingReasons.push('duration_unknown')
  } else if (durationSec < EFFECTIVE_MIN_DURATION_SECONDS) {
    blockingReasons.push('duration_too_short')
  } else if (durationSec > EFFECTIVE_MAX_DURATION_SECONDS) {
    blockingReasons.push('duration_too_long')
  }

  if (args.captureOk && args.decodeOk) {
    if (args.meanRms < 0.01) {
      blockingReasons.push('no_speech')
    } else if (args.meanRms < 0.02) {
      warnings.push('volume_low')
    } else if (args.meanRms > 0.5) {
      warnings.push('volume_high')
    }

    if (args.peak >= 0.98) {
      warnings.push('clipping')
    }

    if (args.silenceRatio > 0.85 && durationSec >= EFFECTIVE_MIN_DURATION_SECONDS) {
      blockingReasons.push('too_much_silence')
    }
  }

  let status: VoiceReferenceQualityStatus = 'GOOD'
  if (blockingReasons.length > 0) status = 'BLOCKED'
  else if (warnings.length > 0) status = 'WARNING'

  const failureCode = resolveFailureCode(blockingReasons, warnings)

  const volumeCheck =
    blockingReasons.includes('no_speech') || blockingReasons.includes('too_much_silence')
      ? 'blocked'
      : warnings.includes('volume_low')
        ? 'warning'
        : warnings.includes('volume_high')
          ? 'warning'
          : 'ok'

  return {
    status,
    durationMs: args.durationMs,
    blockingReasons,
    warnings,
    meanRms: args.meanRms,
    peak: args.peak,
    silenceRatio: args.silenceRatio,
    captureOk: args.captureOk,
    decodeOk: args.decodeOk,
    failureCode,
    speechSeconds: args.speechSeconds,
    speechRatio: args.speechRatio,
    sampleRate: args.sampleRate,
    channels: args.channels,
    checks: {
      duration:
        blockingReasons.includes('duration_too_short') || blockingReasons.includes('duration_too_long')
          ? 'blocked'
          : args.durationMs > 0
            ? formatDurationClock(args.durationMs)
            : 'blocked',
      speech:
        blockingReasons.includes('no_speech') || blockingReasons.includes('too_much_silence')
          ? 'blocked'
          : args.speechSeconds != null
            ? `${args.speechSeconds.toFixed(1)}s`
            : 'ok',
      volume: volumeCheck,
      clipping: warnings.includes('clipping') ? 'warning' : 'ok',
      format: args.captureOk ? 'ok' : 'blocked',
      decode: args.decodeOk ? 'ok' : 'blocked',
    },
  }
}

export function formatDurationClock(totalMs: number): string {
  const totalSec = Math.max(0, Math.floor(totalMs / 1000))
  const mm = String(Math.floor(totalSec / 60)).padStart(2, '0')
  const ss = String(totalSec % 60).padStart(2, '0')
  return `${mm}:${ss}`
}
